import { Injectable } from '@angular/core';
import { captureException } from '@sentry/angular';
import { environment } from 'src/environments/environment';
import { firstValueFrom } from 'rxjs';

import { AppConfigurationService } from '../sessions/session/iframe/additional-apps.utils';

import { SpacesService } from './spaces.service';
import { FLAGS, FlagsService } from './flags.service';

export interface Position {
  top: number;
  left: number;
}

export interface CenterPosition {
  x: number;
  y: number;
}
export interface AppIcon {
  type: AppIconSource;
  content: string;
  height: number;
  width: number;
}
export enum AppIconSource {
  ICON = 'ICON',
  ASSET_IMG = 'ASSET-IMG',
  HTTP_IMG = 'HTTP-IMG',
}

export interface OverlayApp {
  title: string;
  icon: string;
  iconSource: AppIconSource;
}

@Injectable({
  providedIn: 'root',
})
export class ItemsCanvasService {
  private signedUrlsCache = new Map<string, string>();

  private disableResourceSigning: boolean = this.flagsService.isFlagEnabled(
    FLAGS.DISABLE_RESOURCE_SIGNING,
  );

  constructor(
    private spacesService: SpacesService,
    private flagsService: FlagsService,
    private appConfigurationService: AppConfigurationService,
  ) {}

  createOverlayAppFromAppsConfig(appId: string, itemState?: { title: string }): OverlayApp | null {
    const app = this.appConfigurationService.getAppByUid(appId);
    if (app?.icon) {
      const iconSource = this.getIconSource(app.icon as string);

      const iconsMap = {
        collaborative_browser: 'web-viewer',
        code_iframe: 'overlay_iframe',
      };

      return {
        title: itemState?.title ?? app.title,
        icon: iconsMap[app.icon] ?? app.icon,
        iconSource,
      };
    }
    return null;
  }

  private async _getTrustedUrl(dataUrl: string, isPublicUrl = false): Promise<string> {
    if (this.isPublicUrl(dataUrl, isPublicUrl)) {
      // Return URL if it is a public resource (like resource library resource)
      return dataUrl;
    } else {
      const [trustedUrl] = await firstValueFrom(this.spacesService.getSignedUrl(dataUrl));
      return trustedUrl;
    }
  }

  async getTrustedUrl(dataUrl: string, isPublicUrl = false): Promise<string> {
    const cachedSignedUrl = this.signedUrlsCache.get(dataUrl);
    if (cachedSignedUrl) {
      return cachedSignedUrl;
    }
    try {
      const trustedURL = await this._getTrustedUrl(dataUrl, isPublicUrl);
      // Only cache the response if _getTrustedUrl was done successfully
      this.signedUrlsCache.set(dataUrl, trustedURL);
      return trustedURL;
    } catch (err) {
      captureException(err);
      return dataUrl;
    }
  }

  public isPublicUrl(dataUrl: string, isPublicUrl = false) {
    // CDN-signed URLs are already public — no further signing needed
    if (dataUrl.startsWith(environment.cdnResourcesDomain)) {
      return true;
    }

    const hasExpiresToken = !!new URL(dataUrl).searchParams.get('Expires');
    return (
      // depending on isPublicUrl is not enough, because of a previous bug that caused the url of the pdf to be stored in the publicUrl field,
      // causing it to think the url is public even though it might not be
      !hasExpiresToken &&
      (isPublicUrl ||
        this.disableResourceSigning ||
        dataUrl.startsWith(environment.googleStorageBucketURL))
    );
  }

  public getIconSource(icon?: string): AppIconSource {
    if (!icon) {
      return AppIconSource.ICON;
    }
    try {
      new URL(icon);
      return AppIconSource.HTTP_IMG;
    } catch (error) {
      return icon.includes('.') ? AppIconSource.ASSET_IMG : AppIconSource.ICON;
    }
  }
}
